// HomeCoverMessage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import SwagLoading from '../component/SwagLoading.jsx';
import LazyImage from '../component/LazyImage.jsx';
import StatefulImage from '../component/StatefulImage.jsx';
import TrailerBlocker from '../container/TrailerBlocker.js';
import {
  color,
  textColor,
  showcasePlayerMobileHeight,
  showcasePlayerTabletHeight,
  showcasePlayerDesktopHeight,
} from '../style/variables';
import ResetButtonStyle from '../style/ResetButtonStyle.js';
import media from '../style/media.js';
import { formatTimeString } from '../resource/formatTimeString.js';
import unlockIcon from '../../img/ic-unlock.svg';
import likeIcon from '../../img/ic-like.svg';
import diamondIcon from '../../img/ic_diamond_sm.svg';
import playIcon from '../../img/ic_play_black.svg';
import bluetickIcon from '../../img/ic_pro_badge.svg';
import viewIcon from '../../img/ic_streaming_view_xs.svg';
import FlixBadge from '../container/FlixBadge.js';

export class HomeCoverMessage extends React.PureComponent {
  constructor(props) {
    super(props);

    const { thumbnailSource, thumbnailSourceBlurred } = this.props;
    const [avifSrcSet, webpSrcSet, jpgSrcSet] = this.calculateSrcSet({
      href: thumbnailSource,
      size: 1024,
    });
    const [avifSrcSetBlurred, webpSrcSetBlurred, jpgSrcSetBlurred] =
      this.calculateSrcSet({
        href: thumbnailSourceBlurred,
        size: 256,
      });

    this.state = {
      avifSrcSet,
      webpSrcSet,
      jpgSrcSet,
      avifSrcSetBlurred,
      webpSrcSetBlurred,
      jpgSrcSetBlurred,
    };
  }
  calculateSrcSet = ({ href, size }) =>
    [MediaAssetFormat.AVIF, MediaAssetFormat.WEBP, MediaAssetFormat.JPG].map(
      format => getPublicSrcSet({ href, size, format })
    );
  nextTick = null;
  player = null;
  renderPlaceholder = () => {
    const { shouldShowUnlockCount } = this.props;
    return (
      <StyledHomeCoverMessage>
        <PlayerBlock>
          <SwagLoadingWrapper>
            <SwagLoading />
          </SwagLoadingWrapper>
        </PlayerBlock>
        <CaptionWrapper isEmpty>
          <Caption></Caption>
        </CaptionWrapper>
        <MetaWrapper>
          <Meta isLeft>
            <MetaIcon src={!shouldShowUnlockCount ? viewIcon : unlockIcon} />
            <Amount>-</Amount>
          </Meta>
          <ActionButtonWrapper />
          <Meta>
            <MetaIcon src={likeIcon} />
            <Amount>-</Amount>
          </Meta>
        </MetaWrapper>
      </StyledHomeCoverMessage>
    );
  };

  renderThumbnail = () => {
    const { username, title, description } = this.props;
    const {
      avifSrcSet,
      webpSrcSet,
      jpgSrcSet,
      avifSrcSetBlurred,
      webpSrcSetBlurred,
      jpgSrcSetBlurred,
    } = this.state;
    const altText = title || description;

    return (
      <Thumbnail>
        <StatefulImage>
          <LazyImage
            data-key="target"
            {...jpgSrcSet}
            threshold={0.01}
            alt={`${username} : ${altText}`}
          >
            <source {...avifSrcSet} type="image/avif" />
            <source {...webpSrcSet} type="image/webp" />
          </LazyImage>
          <LazyImage data-key="error" {...jpgSrcSetBlurred} threshold={0.25}>
            <source {...avifSrcSetBlurred} type="image/avif" />
            <source {...webpSrcSetBlurred} type="image/webp" />
          </LazyImage>
        </StatefulImage>
      </Thumbnail>
    );
  };

  renderActionButton = () => {
    const { isUnlocked, messageId, t, unlockPrice } = this.props;
    const hasMessageId = !!messageId;
    const LinkProps = {
      as: isUnlocked ? PlayActionButton : UnlockActionButton,
      to: `/post/${messageId}`,
      onClick: () => {
        trackMixpanel({
          type: EventTypes.USER_CLICKED,
          payload: {
            viewId: getViewId({ pathname: window.location.pathname }),
            buttonId: ButtonId.All.ButtonFreeWatch,
            messageId,
          },
        });
      },
    };
    return (
      <ActionButtonWrapper>
        {/* $isUnlocked is a transient prop https://styled-components.com/docs/api#transient-props */}
        <ActionButton
          hasMessageId={hasMessageId}
          $isUnlocked={isUnlocked}
          {...(hasMessageId ? LinkProps : {})}
        >
          {isUnlocked ? (
            <>
              <PlayIcon src={playIcon} />
              {t('button_free_watch')}
            </>
          ) : (
            <>
              {t('message_detail_unlock')}
              <DiamondIcon src={diamondIcon} />
              <UnlockPrice>{unlockPrice}</UnlockPrice>
            </>
          )}
        </ActionButton>
      </ActionButtonWrapper>
    );
  };
  trackUserClick = buttonId => {
    const { index, category, username, messageId, unlockPrice } = this.props;

    const abData = { unlockPrice };
    return trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId,
        messageId,
        abData,
        extraProps: {
          'user.username': username,
          'discover.index': index,
          'discover.category': category,
        },
      },
    });
  };
  handleClickMessage = (event, buttonId) => {
    const {
      isActive,
      isUIInteractable,
      isFlix,
      messageId,
      push,
      showcaseFeed,
    } = this.props;
    if (isActive && isUIInteractable) {
      event.stopPropagation();

      this.trackUserClick(
        buttonId
          ? buttonId
          : isFlix
          ? ButtonId.All.ButtonFlixCard
          : ButtonId.All.ButtonStoryCard
      );

      if (isFlix) {
        return push(`/post/${messageId}`);
      }

      return push(`/story/${messageId}`, {
        listPath: ['home', 'messages', showcaseFeed],
      });
    }
  };

  componentDidMount() {
    this.nextTick = setTimeout(() => {
      const {
        messageId,
        username,
        authorId,
        isUnlocked,
        fetchMessageDetail,
        fetchDrmLicenseAvailability,
        fetchUser,
        fetchAssetManifest,
        duration,
        assetId,
      } = this.props;
      if (!username && authorId) {
        fetchUser({ authorId });
      }
      if (messageId) {
        fetchMessageDetail();
        if (!isUnlocked) {
          fetchDrmLicenseAvailability();
        }
        if (!duration && assetId) {
          fetchAssetManifest({ messageId, assetId });
        }
      }
    }, 0);
  }

  componentDidUpdate(prevProps) {
    const {
      isActive,
      isUIInteractable,
      messageId,
      isAuthed,
      isUnlocked,
      fetchDrmLicenseAvailability,
      thumbnailSource,
      thumbnailSourceBlurred,
    } = this.props;
    if (
      isActive &&
      isUIInteractable !== prevProps.isUIInteractable &&
      this.canPlayThrough
    ) {
      return isUIInteractable ? this.player.play() : this.player.pause();
    }
    if (messageId && isAuthed !== prevProps.isAuthed && !isUnlocked) {
      fetchDrmLicenseAvailability();
    }
    if (thumbnailSource !== prevProps.thumbnailSource) {
      const [avifSrcSet, webpSrcSet, jpgSrcSet] = this.calculateSrcSet({
        href: thumbnailSource,
        size: 1024,
      });
      this.setState({ avifSrcSet, webpSrcSet, jpgSrcSet });
    }
    if (thumbnailSourceBlurred !== prevProps.thumbnailSourceBlurred) {
      const [avifSrcSetBlurred, webpSrcSetBlurred, jpgSrcSetBlurred] =
        this.calculateSrcSet({
          href: thumbnailSourceBlurred,
          size: 256,
        });
      this.setState({ avifSrcSetBlurred, webpSrcSetBlurred, jpgSrcSetBlurred });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  renderBadge = () => {
    const { badges, messageBadgeNotShowList } = this.props;
    return (
      <BadgeWrapper>
        {badges
          .filter(value => !messageBadgeNotShowList.includes(value))
          .map(value => (
            <FlixBadge key={value} badgeName={value} />
          ))}
      </BadgeWrapper>
    );
  };

  render() {
    const {
      messageId,
      description,
      title,
      unlockTimes,
      viewTimes,
      likeRatio,
      unlockPrice,
      duration,
      username,
      isUnlocked,
      isActive,
      isFlix,
      isPro,
      shouldShowUnlockCount,
      t,
    } = this.props;

    if (!messageId) {
      return this.renderPlaceholder();
    }

    let messageTitle = title || description;

    const isFreeMessage = unlockPrice === 0;
    const isMessageTitleEmpty = !messageTitle;

    if (isMessageTitleEmpty) {
      if (isFlix) {
        messageTitle = t('flix_caption_placeholder', { username });
      } else {
        messageTitle = t('message_caption_placeholder', { username });
      }
    }

    return (
      <StyledHomeCoverMessage
        isActive={isActive}
        onClick={this.handleClickMessage}
      >
        {this.renderBadge()}
        <PlayerBlock>
          {this.renderThumbnail()}
          {!isUnlocked && <TrailerBlocker zIndex="0" />}
          {duration && <Duration>{formatTimeString(duration * 1000)}</Duration>}
          {/* To overlay on video player */}
          <Background />
        </PlayerBlock>
        <CaptionWrapper isEmpty={isMessageTitleEmpty}>
          {isPro && <ProIcon src={bluetickIcon} alt="pro icon" />}
          <Caption>{messageTitle}</Caption>
        </CaptionWrapper>
        <MetaWrapper>
          <Meta isLeft>
            {(!shouldShowUnlockCount || !isFreeMessage) && (
              <>
                <MetaIcon
                  src={!shouldShowUnlockCount ? viewIcon : unlockIcon}
                />
                <Amount>
                  {!shouldShowUnlockCount ? viewTimes : unlockTimes}
                </Amount>
              </>
            )}
          </Meta>
          {this.renderActionButton()}
          <Meta>
            {!isFreeMessage && (
              <>
                <MetaIcon src={likeIcon} />
                <Amount>{likeRatio}</Amount>
              </>
            )}
          </Meta>
        </MetaWrapper>
      </StyledHomeCoverMessage>
    );
  }
}

HomeCoverMessage.propTypes = {
  t: PropTypes.func.isRequired,
  index: PropTypes.number,
  category: PropTypes.string,
  authorId: PropTypes.string,
  messageId: PropTypes.string,
  assetId: PropTypes.string,
  description: PropTypes.string,
  title: PropTypes.string,
  username: PropTypes.string,
  thumbnailSource: PropTypes.string,
  thumbnailSourceBlurred: PropTypes.string,
  sendUnlockMessage: PropTypes.func,
  fetchDrmLicenseAvailability: PropTypes.func,
  fetchAssetManifest: PropTypes.func,
  fetchUser: PropTypes.func,
  push: PropTypes.func,
  fetchMessageDetail: PropTypes.func,
  unlockPrice: PropTypes.number,
  duration: PropTypes.number,
  unlockTimes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewTimes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  likeRatio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isUnlocked: PropTypes.bool,
  isActive: PropTypes.bool,
  isUIInteractable: PropTypes.bool,
  isFlix: PropTypes.bool,
  isAuthed: PropTypes.bool,
  isPro: PropTypes.bool,
  shouldShowUnlockCount: PropTypes.bool,
  showcaseFeed: PropTypes.string,
  badges: PropTypes.array,
  messageBadgeNotShowList: PropTypes.array,
  unlockButtonStatus: PropTypes.string,
};

HomeCoverMessage.defaultProps = {
  index: null,
  category: null,
  messageId: null,
  assetId: null,
  authorId: null,
  unlockPrice: 0,
  duration: 0,
  unlockTimes: '-',
  viewTimes: '-',
  likeRatio: '-',
  description: '',
  title: '',
  username: '',
  thumbnailSource: '',
  thumbnailSourceBlurred: '',
  isUnlocked: false,
  isActive: false,
  isUIInteractable: false,
  isFlix: false,
  isAuthed: false,
  unlockButtonStatus: '',
  sendUnlockMessage: () => null,
  fetchDrmLicenseAvailability: () => null,
  fetchAssetManifest: () => null,
  fetchUser: () => null,
  push: () => null,
  fetchMessageDetail: () => null,
  badges: [],
  messageBadgeNotShowList: [],
  isPro: false,
  shouldShowUnlockCount: false,
  showcaseFeed: '',
};

const borderRadius = 4;

const StyledHomeCoverMessage = styled.article.attrs(({ isActive }) => ({
  style: {
    opacity: isActive ? 1 : 0.5,
    transition: 'opacity 0.6s',
  },
}))`
  border-radius: ${borderRadius}px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  flex: none;
  overflow: hidden;
  position: relative;
`;

const Duration = styled.div`
  position: absolute;
  top: 16px;
  right: 20px;
  padding: 4px 8px;
  border-radius: 2px;
  background-color: rgba(25, 25, 25, 0.6);
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 400;

  ${media.tablet`
    top: 12px;
    right: 12px;
    padding: 4px;
    font-size: 12px;
  `}
`;

const PlayerBlock = styled.div`
  background: rgba(100, 100, 100, 0.2);
  width: 100%;
  height: ${showcasePlayerDesktopHeight}px;
  position: relative;

  ${media.tablet`
    height: ${showcasePlayerTabletHeight}px;
  `}
  ${media.mobile`
    height: ${showcasePlayerMobileHeight}px;
  `}
`;

const SwagLoadingWrapper = styled.div`
  width: 120px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate3d(-50%, -50%, 0);
`;

const Thumbnail = styled.div`
  border-top-left-radius: ${borderRadius}px;
  border-top-right-radius: ${borderRadius}px;
  /* Make size a little smaller to fix pixel glitch of video (video can't be exact 100% width) */
  position: absolute;
  left: 1px;
  width: calc(100% - 2px);
  height: 100%;
`;

const BadgeWrapper = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  z-index: 1;
`;

const CaptionWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 0px 80px;
  gap: 4px;

  position: absolute;
  left: 0px;
  right: 0px;
  bottom: 72px;
  ${media.tablet`
    padding: 0px 12px;
    bottom: 64px;
  `};
  ${media.mobile`
    width: 320px;
    height: 19px;
    top: 157px;
    bottom: unset;
  `};
`;

const Caption = styled.div`
  max-width: 508px;
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  text-align: center;
  letter-spacing: 0.0015em;
  flex: none;
  order: 1;
  align-self: stretch;
  flex-grow: 0;
  color: ${color.white};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  ${media.tablet`
    max-width: 276px;
    max-height: 41px;
    font-size: 14px;
    letter-spacing: 0.001em;
  `};
  ${media.mobile`
    max-height: 19px;
    -webkit-line-clamp: 1;
  `}
`;

const ProIcon = styled.img`
  margin-top: 3px;
  flex: none;
  order: 0;
  flex-grow: 0;
  width: 24px;
  height: 24px;
  ${media.tablet`
    margin-top: 1px;
    width: 16px;
    height: 16px;
  `};
`;

const MetaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0px 20px;
  gap: 20px;

  position: absolute;
  height: 48px;
  left: 0px;
  right: 0px;
  bottom: 16px;
  ${media.tablet`
    padding: 8px 20px;
    height: 64px;
    bottom: 0px;
    gap: 12px;
  `};
  ${media.mobile`
    padding: 8px 16px;
    width: 320px;
    left: calc(50% - 320px / 2);
    top: calc(50% - 64px / 2 + 88px);
  `}
`;

const Meta = styled.div.attrs(({ isLeft }) => ({
  style: {
    order: isLeft ? 0 : 2,
  },
}))`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px;
  gap: 4px;
  width: 32px;
  height: 48px;
  opacity: 0.5;
  flex: none;
  flex-grow: 0;
`;

const MetaIcon = styled.img`
  width: 32px;
`;

const Amount = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1;
  color: ${color.white};
  font-size: 12px;
  text-align: center;
`;

const ActionButtonWrapper = styled.div`
  min-width: 160px;
  height: 36px;
  font-size: 20px;
  ${media.tablet`
    font-size: 18px;
  `}
`;

const FilledButton = css`
  background-color: ${textColor.white};
`;

const OutlineButton = css`
  background-color: rgba(71, 71, 71, 0.3);
  border: 1px solid ${color.tealBlue};
`;

const ActionButton = styled(({ hasMessageId, ...restProps }) =>
  hasMessageId ? <Link {...restProps} /> : <div {...restProps} />
)`
  ${ResetButtonStyle}
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 8px;
  gap: 8px;
  width: 320px;
  height: 40px;
  border-radius: 4px;
  ${props => (props.$isUnlocked ? FilledButton : OutlineButton)}
  ${media.tablet`
    padding: 8px;
    gap: 4px;
    width: 272px;
    height: 36px;
    flex-grow: 1;
  `};
  ${media.mobile`
    width: 192px;
  `};
`;

const UnlockActionButton = styled(ActionButton)`
  color: ${textColor.primary};
  background-color: rgba(100, 100, 100, 0.3);
`;

const PlayActionButton = styled(ActionButton)`
  color: ${color.black};
  background-color: ${color.white};
`;

const Background = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 25.88%,
    rgba(0, 0, 0, 0.8) 100%
  );
`;

const PlayIcon = styled.img`
  width: 20px;
  height: 20px;
`;

const DiamondIcon = styled.img`
  width: 24px;
  height: 24px;
  ${media.tablet`
    width: 20px;
    height: 20px;
  `};
`;

const UnlockPrice = styled.span`
  font-size: 22px;
  ${media.tablet`
    font-size: 20px;
  `};
`;

export default withFetchTranslation(HomeCoverMessage);
